import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  heading: "#2E2E2E",
  text: "#4B5563",
  sectionTitle: "#1F7A75",
  background: "#3AAFA9",
};

const sosSections = [
  {
    title: "1. Nature of Service",
    text:
      "Janak provides a technology-enabled emergency coordination service that connects registered users with trained Janak Partners for immediate on-site assistance. Janak is not a hospital, ambulance operator, emergency medical service (EMS), or healthcare institution.",
  },
  {
    title: "2. Scope of SOS Service",
    text: "Upon activation of the SOS feature, Janak will make reasonable efforts to:",
    list: [
      "Dispatch the nearest available Janak Partner.",
      "Notify the registered child(ren) and emergency contacts.",
      "Coordinate with the user's preferred hospital (if registered) or the nearest appropriate hospital.",
      "Coordinate ambulance services wherever available.",
      "Share available medical information with emergency responders.",
      "Provide real-time updates to authorised family members.",
    ],
  },
  {
    title: "3. Responsibilities of Janak Partner",
    text: "The Janak Partner may:",
    list: [
      "Reach the user's location.",
      "Assess the immediate situation.",
      "Help maintain a safe environment until emergency services arrive.",
      "Monitor basic vital parameters (BP, Blood Sugar, SpO₂) if appropriate and equipment is available.",
      "Assist with patient movement only when safe and medically appropriate.",
      "Coordinate ambulance arrival and hospital admission.",
      "Carry or provide access to the user's medical records available within the Janak application.",
    ],
  },
  {
    title: "4. Limitations of Janak Partner",
    text:
      "Unless separately booked under a clinical home-care service and performed by an appropriately qualified professional, the Janak Partner shall not:",
    list: [
      "Diagnose any illness.",
      "Prescribe medicines.",
      "Administer injections or IV fluids.",
      "Perform invasive medical procedures.",
      "Make treatment decisions.",
      "Sign hospital admission forms or consent documents.",
      "Accept legal or financial responsibility on behalf of the user or family.",
      "Override the advice of qualified medical professionals.",
    ],
  },
  {
    title: "5. Duration of Assistance",
    text: "The Janak Partner's emergency engagement shall conclude upon the earliest of:",
    list: [
      "Safe handover to ambulance personnel;",
      "Formal handover to hospital staff after admission;",
      "Handover to an authorised family member or caregiver; or",
      "Completion of the maximum emergency assistance duration as defined in the applicable service plan or operational policy.",
    ],
    afterText: "Any additional assistance shall be subject to Janak's availability and separate service terms.",
  },
  {
    title: "6. User Responsibilities",
    text: "The user agrees to:",
    list: [
      "Provide accurate medical information.",
      "Maintain updated emergency contacts.",
      "Register a preferred hospital, where applicable.",
      "Inform Janak of allergies, chronic illnesses, medications, and other relevant health conditions.",
      "Obtain all necessary consents from the parent or elder for sharing medical information with authorised family members and healthcare providers.",
    ],
  },
  {
    title: "7. Emergency Contact Procedure",
    text:
      "If the registered child is unavailable, Janak will attempt to contact alternate emergency contacts. Where immediate medical attention is reasonably necessary, Janak may coordinate with emergency responders and the nearest suitable healthcare facility without waiting for family confirmation, solely to avoid delay in obtaining professional medical care.",
  },
  {
    title: "8. Medical Decision-Making",
    text: "All medical decisions shall remain solely with:",
    list: [
      "The patient (where capable),",
      "The treating doctor,",
      "The hospital,",
      "Or the patient's legally authorised representative.",
    ],
    afterText: "Janak and its personnel shall not make medical decisions on behalf of the patient.",
  },
  {
    title: "9. Hospital Selection",
    text:
      "Where a preferred hospital has been registered, Janak will make reasonable efforts to coordinate with that hospital. However, if immediate medical intervention is reasonably required or the preferred hospital is unavailable, inaccessible, or unsuitable, emergency responders or attending medical professionals may determine the most appropriate healthcare facility.",
  },
  {
    title: "10. Data Privacy",
    text:
      "Medical records, emergency information, and personal details shall be processed in accordance with Janak's Privacy Policy and applicable Indian laws, including the Digital Personal Data Protection Act, 2023. Information shall only be shared with authorised family members, emergency responders, hospitals, healthcare professionals, or where required by law.",
  },
  {
    title: "11. Service Availability",
    text:
      "SOS assistance is provided on a best-efforts basis. Response time may vary depending upon Location, Traffic conditions, Weather, Availability of Janak Partners, Availability of ambulances, Network connectivity, Government restrictions, or Force majeure events. Janak does not guarantee a fixed response time.",
  },
  {
    title: "12. Limitation of Liability",
    text:
      "Janak provides coordination and support services only. Janak shall not be liable for Medical outcomes, Clinical decisions, Delays caused by hospitals or ambulance providers, Acts or omissions of third-party healthcare providers, or Circumstances beyond its reasonable control. Nothing in these Terms excludes liability that cannot lawfully be excluded under applicable law.",
  },
  {
    title: "13. Service Suspension",
    text: "Janak reserves the right to suspend or terminate SOS services in cases of:",
    list: [
      "Fraudulent use or False emergency requests,",
      "Abuse of Janak personnel,",
      "Unsafe operating conditions or Violation of these Terms.",
    ],
  },
  {
    title: "14. Governing Law",
    text:
      "These Terms shall be governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the competent courts where Janak's registered office is located.",
  },
];

const SosTerms = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headingRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headingRef.current,
        { opacity: 0, y: -30 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
      );
      gsap.fromTo(
        introRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 1.2, delay: 0.2, ease: "power3.out" }
      );
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, scale: 0.95 },
        { opacity: 1, scale: 1, duration: 1.4, delay: 0.4, ease: "power3.out" }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        backgroundColor: COLORS.background,
        padding: "80px 20px",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <div
        ref={cardRef}
        style={{
          maxWidth: "1000px",
          width: "100%",
          backgroundColor: "#FFFFFF",
          borderRadius: "24px",
          padding: "50px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <h1
            ref={headingRef}
            style={{
              color: COLORS.heading,
              fontSize: "2.8rem",
              fontWeight: "800",
              marginBottom: "15px",
              letterSpacing: "-0.5px",
            }}
          >
            JANAK – SOS Emergency Assistance Service
          </h1>
          <h2
            ref={introRef}
            style={{
              color: COLORS.primary,
              fontSize: "1.4rem",
              fontWeight: "700",
              marginBottom: "10px",
            }}
          >
            Terms & Conditions
          </h2>
          <p style={{ color: COLORS.text, fontSize: "0.95rem" }}>
            Effective Date: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div style={{ borderTop: "2px solid #E5E7EB", paddingTop: "30px" }}>
          {sosSections.map((section, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: "35px",
                backgroundColor: "#F9FAF5",
                padding: "25px",
                borderRadius: "16px",
                border: "1px solid #E2E8F0",
              }}
            >
              <h3
                style={{
                  color: COLORS.sectionTitle,
                  fontSize: "1.35rem",
                  fontWeight: "700",
                  marginBottom: "12px",
                }}
              >
                {section.title}
              </h3>
              {section.text && (
                <p
                  style={{
                    color: COLORS.text,
                    fontSize: "1.05rem",
                    lineHeight: "1.7",
                    marginBottom: section.list ? "12px" : "0",
                  }}
                >
                  {section.text}
                </p>
              )}
              {section.list && (
                <ul
                  style={{
                    paddingLeft: "24px",
                    margin: "0",
                    color: COLORS.text,
                    lineHeight: "1.8",
                    fontSize: "1.05rem",
                  }}
                >
                  {section.list.map((item, i) => (
                    <li key={i} style={{ marginBottom: "6px" }}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {section.afterText && (
                <p
                  style={{
                    color: COLORS.text,
                    fontSize: "1.05rem",
                    lineHeight: "1.7",
                    marginTop: "12px",
                    fontWeight: "500",
                  }}
                >
                  {section.afterText}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SosTerms;
